/**
 * ClientePortalPage — `/cliente` (vista de consulta del cliente, §3 del PRD).
 *
 * La cuenta del cliente final: solo consulta (NO opera campañas).
 * Ve su reporte comercial, el historial de campañas y los resultados.
 * Datos REALES del backend (`GET /activa/campaigns`), agregados desde stats.
 */

import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { Rocket, TrendingUp, FileText, ArrowRight, Loader2 } from "lucide-react";
import { formatARS, formatCompact } from "../mockData";
import { activaApi } from "../api";
import { marcaQueFirma } from "../utils/marcaCampana";
import { plural } from "../utils/format";

function fmtFecha(iso?: string): string {
  if (!iso) return "";
  try {
    return new Date(iso).toLocaleDateString("es-AR", { day: "2-digit", month: "short", year: "numeric" });
  } catch { return ""; }
}

export default function ClientePortalPage() {
  const [camps, setCamps] = useState<any[]>([]);
  const [loading, setLoading] = useState(true);
  /** el fetch falló ≠ "no hay campañas": sin esto, un backend caído se veía como un negocio sin resultados */
  const [error, setError] = useState("");

  useEffect(() => {
    let vivo = true;
    (async () => {
      try {
        const r = await activaApi.campaigns();
        // La campaña de los datos de ejemplo (HATSU, `esEjemplo`) no es del comercio: se mostraba en su historial como
        // si fuera real, y hasta lo saludaba «Hola, HATSU» (QA del 08/10). Acá no aparece.
        const reales = (Array.isArray(r.data) ? r.data : []).filter((c: any) => c?.esEjemplo !== true);
        if (vivo) { setCamps(reales); setError(""); }
      } catch {
        // Sin este catch, un fallo de red dejaba `camps` vacío y la pantalla mostraba
        // "Todavía no hay campañas" y "$ 0" — al CLIENTE. Un backend caído no es un negocio
        // sin resultados.
        if (vivo) setError("No pudimos cargar tus campañas en este momento.");
      } finally {
        if (vivo) setLoading(false);
      }
    })();
    return () => { vivo = false; };
  }, []);

  // El nombre del comercio: el de sus campañas o el que se guardó al entrar. Nunca el id («hatsu-demo») y
  // nunca un relleno: «Hola, Tu negocio 👋» le hablaba al cliente como a una plantilla sin completar. Tampoco
  // el «Tu marca» que guardaban las campañas sin nombre (QA del 08/10: «Hola, Tu marca»): `marcaQueFirma`.
  const nombreGuardado = activaApi.nombreActual();
  const marca = marcaQueFirma(camps.find((c) => marcaQueFirma(c.marca))?.marca)
    || (nombreGuardado !== activaApi.marcaActual() ? marcaQueFirma(nombreGuardado) : "");

  /**
   * GATE DE ATRIBUCIÓN — sólo se suma lo que se midió DE VERDAD.
   *
   * Antes se sumaba `plataRecuperada` de todas las campañas por igual: una campaña sin medir
   * aporta 0, y ese 0 se mostraba como "recuperaste $0" en un panel verde a pantalla completa.
   * El centro de control ya distinguía "sin medir" de "medido"; esta pantalla —la única que ve
   * quien paga— no. Dos pantallas, dos verdades, y la falsa era la del cliente.
   *
   * Y lo que se suma es lo que COMPRARON los que recibieron después del envío (`measure`: lo gastado entre la base de
   * la campaña y la siguiente), no lo que trajo la campaña: no hay grupo de control, así que incluye a los que habrían
   * comprado igual. Decía «Plata recuperada en total» y «volvieron a comprar gracias a tus campañas»; ahora dice lo que
   * es, con la aclaración a la vista (decisión de Alan del 29/10, aunque la cifra se lea más chica).
   *
   * Lo que sí se compara contra un grupo de control (el Resultado de una campaña con segmentación,
   * `envios/services/resultado.service.ts`) no entra acá: `GET /activa/campaigns` (`listCampaigns`) sólo trae las de
   * Reactivación y deja afuera las que salieron de una segmentación (`origen.tipo === "segmentacion"`). Si algún día
   * este portal suma esas, lo que va como «gracias a la campaña» es su incremental, no esta suma.
   */
  const medidas = camps.filter((c) => c.atribucion === "exacta-por-snapshot");
  const totalCompraron = medidas.reduce((s, c) => s + (c.stats?.plataRecuperada || 0), 0);
  const totalVolvieron = medidas.reduce((s, c) => s + (c.stats?.volvieron || 0), 0);
  const enviadas = camps.filter((c) => ["sent", "measured"].includes(c.estado));
  const sinMedir = enviadas.length - medidas.length;
  const reporteToken = camps.find((c) => c.shareToken)?.shareToken || "";

  return (
    <div className="min-h-screen bg-slate-50">
      <header className="sticky top-0 z-10 bg-white/80 backdrop-blur-md border-b border-slate-200/60">
        <div className="mx-auto max-w-4xl px-4 md:px-6 h-14 flex items-center justify-between">
          <span className="text-sm font-bold text-slate-900 flex items-center gap-2">
            <span className="w-6 h-6 rounded-lg bg-violet-600 text-white flex items-center justify-center"><Rocket size={13} /></span>
            {marca ? `${marca} · Panel` : "Tu panel"}
          </span>
          <span className="text-xs text-slate-400">Cuenta de consulta</span>
        </div>
      </header>

      <main className="mx-auto max-w-4xl px-4 md:px-6 py-8 space-y-6">
        <div>
          <h1 className="text-2xl font-bold text-slate-900 tracking-tight">{marca ? `Hola, ${marca} 👋` : "Hola 👋"}</h1>
          <p className="text-sm text-slate-500 mt-1">Esto es lo que DEENEX Activa viene haciendo por tu negocio.</p>
        </div>

        {loading ? (
          <div className="flex items-center justify-center gap-2 py-20 text-slate-400 text-sm"><Loader2 size={18} className="animate-spin" /> Cargando tu panel…</div>
        ) : error ? (
          <div className="rounded-3xl border-2 border-amber-200 bg-amber-50 p-7 text-center">
            <p className="text-base font-bold text-amber-900">{error}</p>
            <p className="text-sm text-amber-800 mt-1.5">Es un problema nuestro, no de tus campañas. Probá recargar en un minuto.</p>
          </div>
        ) : (
          <>
            {/* Lo que compraron después — SÓLO si hay al menos una campaña medida de verdad.
                Un panel verde con "$ 0" cuando lo que pasa es que todavía no se midió le dice al
                cliente que su campaña no sirvió. La verdad es otra y hay que decirla.
                Y la aclaración va escrita, no en un `title`: sin ella, la cifra se lee como lo que trajo la campaña. */}
            {medidas.length > 0 ? (
              <div className="relative overflow-hidden rounded-3xl bg-gradient-to-br from-emerald-500 to-emerald-700 p-7 text-white shadow-lg">
                <div className="absolute -right-6 -top-6 w-40 h-40 bg-white/10 rounded-full blur-2xl" />
                <div className="relative">
                  <p className="text-sm font-medium text-emerald-50 uppercase tracking-wider flex items-center gap-2"><TrendingUp size={15} /> Compraron después de recibir {plural(medidas.length, "tu campaña", "tus campañas")}</p>
                  <p className="text-4xl md:text-5xl font-black tracking-tight mt-2 tabular-nums">{formatCompact(totalCompraron)}</p>
                  <p className="text-sm text-emerald-50 mt-2">
                    {totalVolvieron} {plural(totalVolvieron, "cliente volvió", "clientes volvieron")} a comprar después de recibir {plural(medidas.length, "tu campaña", "tus campañas")}.
                    {medidas.length < enviadas.length && <> Medido sobre {medidas.length} de {enviadas.length} campañas.</>}
                  </p>
                  <p data-testid="aclaracion-sin-control" className="text-xs text-emerald-50/90 mt-3 pt-3 border-t border-white/20 max-w-xl leading-relaxed">
                    Incluye a quienes habrían comprado igual: {plural(medidas.length, "esta campaña no se compara", "estas campañas no se comparan")} contra
                    un grupo que no {plural(medidas.length, "la", "las")} recibió.
                  </p>
                </div>
              </div>
            ) : enviadas.length > 0 ? (
              <div className="rounded-3xl border-2 border-dashed border-slate-200 bg-white p-7">
                <p className="text-sm font-medium text-slate-400 uppercase tracking-wider flex items-center gap-2"><TrendingUp size={15} /> Compras después del envío</p>
                <p className="text-2xl font-bold text-slate-700 mt-2">Todavía no lo medimos</p>
                <p className="text-sm text-slate-500 mt-2 max-w-lg">
                  Ya {plural(enviadas.length, "salió tu campaña", `salieron tus ${enviadas.length} campañas`)}, pero para saber
                  quién volvió a comprar necesitamos tu base actualizada del mes siguiente: comparamos persona por
                  persona quién compró después del envío. Sin eso, cualquier número sería un invento.
                </p>
              </div>
            ) : null}

            {/* Acceso al reporte comercial */}
            {reporteToken && (
              <Link to={`/r/${reporteToken}`} className="group flex items-center gap-4 rounded-2xl border border-slate-200 bg-white p-5 shadow-sm hover:border-violet-300 transition-colors">
                <div className="w-11 h-11 rounded-xl bg-violet-50 text-violet-700 flex items-center justify-center flex-shrink-0"><FileText size={20} /></div>
                <div className="flex-1">
                  <p className="text-sm font-bold text-slate-900">Tu reporte comercial</p>
                  <p className="text-xs text-slate-500">La foto de tu base: plata dormida, concentración y tus grupos de clientes.</p>
                </div>
                <ArrowRight size={18} className="text-violet-400 group-hover:translate-x-1 transition-transform" />
              </Link>
            )}

            {/* Historial de campañas */}
            <div>
              <p className="text-xs font-bold uppercase tracking-wider text-slate-400 mb-3">Historial de campañas</p>
              {camps.length === 0 ? (
                <div className="rounded-2xl border border-slate-100 bg-white shadow-sm py-12 text-center text-sm text-slate-400">
                  Todavía no hay campañas. Cuando salga la primera, aparece acá.
                </div>
              ) : (
                <div className="rounded-2xl border border-slate-100 bg-white shadow-sm divide-y divide-slate-50">
                  {camps.map((h) => (
                    <div key={h.id} className="flex items-center gap-4 p-4">
                      <div className="flex-1 min-w-0">
                        <p className="text-sm font-bold text-slate-800">{h.nombre}</p>
                        {/* "Contactados" son a quienes se les MANDÓ el mensaje (stats.enviados), no
                            el tamaño de la base segmentada (stats.contactos). Una campaña recién
                            analizada tiene contactos:245 y enviados:0 → decir "245 contactados" es
                            afirmar un envío que no ocurrió. Antes de enviar mostramos el universo
                            como "en la base"; una vez enviada, los contactados reales. */}
                        <p className="text-xs text-slate-400">{fmtFecha(h.createdAt)} · {(h.stats?.enviados || 0) > 0 ? `${(h.stats?.enviados || 0).toLocaleString("es-AR")} ${plural(h.stats?.enviados || 0, "contactado", "contactados")}` : `${(h.stats?.contactos || 0).toLocaleString("es-AR")} en la base`}</p>
                      </div>
                      {/* Cada fila dice si su número está medido o no. Mostrar "$ 0" en una
                          campaña sin medir es afirmar que nadie compró. El de una medida es lo que compraron
                          los que la recibieron (con la aclaración de arriba: sin grupo de control). */}
                      <div className="text-right flex-shrink-0">
                        {h.atribucion === "exacta-por-snapshot" ? (
                          <>
                            <p className="text-sm font-bold text-emerald-600 tabular-nums">{formatARS(h.stats?.plataRecuperada || 0)}</p>
                            <p className="text-[11px] text-slate-400">{h.stats?.volvieron || 0} {plural(h.stats?.volvieron || 0, "volvió", "volvieron")}</p>
                          </>
                        ) : ["sent", "measured"].includes(h.estado) ? (
                          <>
                            <p className="text-sm font-semibold text-slate-400">—</p>
                            <p className="text-[11px] text-slate-400">sin medir todavía</p>
                          </>
                        ) : (
                          <p className="text-[11px] text-slate-400">todavía no se envió</p>
                        )}
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </div>
          </>
        )}

        <p className="text-center text-xs text-slate-400 pt-2 pb-8">
          Powered by <strong className="text-violet-600">DEENEX Activa</strong>
        </p>
      </main>
    </div>
  );
}
